import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export const BIKE_TYPE_INFO: Record<string, string> = {
  Sport: 'smooth road with medium to high speed turns',
  Sumo: 'smooth or rough road with primarily tight turns',
  ADV: 'rough road, possibly with some dirt, medium to high speed turns',
  Cruiser: 'smooth road with gentle turns and scenic views',
}

interface Props {
  type: string
  children: React.ReactNode
  className?: string
}

// Desktop: styled tooltip on hover. Mobile: tap opens the descriptor and a
// full-screen catcher closes it on the next tap anywhere (including the chip).
export default function BikeTypeTip({ type, children, className }: Props) {
  const [hover, setHover] = useState(false)
  const [tapped, setTapped] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const [shift, setShift] = useState({ x: 0, flip: false })
  const ref = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLSpanElement>(null)

  const measure = () => {
    const r = ref.current?.getBoundingClientRect()
    if (r) {
      setShift({ x: 0, flip: false })
      setPos({ top: r.top, left: r.left + r.width / 2 })
    }
  }

  const text = BIKE_TYPE_INFO[type]
  const open = (hover || tapped) && text

  // Clamp/flip after the tooltip mounts: keep it inside the viewport
  // horizontally, and flip below the chip when there's no room above.
  useLayoutEffect(() => {
    if (!pos || !open) return
    const tip = tipRef.current
    if (!tip) return
    const r = tip.getBoundingClientRect()
    const margin = 8
    const chip = ref.current?.getBoundingClientRect()
    const roomAbove = chip ? chip.top : Infinity
    const flip = roomAbove < r.height + margin + 8
    let x = 0
    if (r.left < margin) x = margin - r.left
    else if (r.right > window.innerWidth - margin) x = window.innerWidth - margin - r.right
    setShift({ x, flip })
  }, [pos, open])

  return (
    <span
      ref={ref}
      className={`relative inline-flex ${className ?? ''}`}
      onMouseEnter={() => {
        measure()
        setHover(true)
      }}
      onMouseLeave={() => setHover(false)}
      onTouchEnd={(e) => {
        e.preventDefault()
        e.stopPropagation()
        measure()
        setTapped((t) => !t)
      }}
    >
      {children}
      {(open && pos) &&
        createPortal(
          <>
            {tapped && (
              <span
                className="fixed inset-0 z-[70]"
                onClick={() => setTapped(false)}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setTapped(false)
                }}
              />
            )}
            <span
              ref={tipRef}
              className="fixed z-[80] w-48 px-4 py-2.5 rounded-lg bg-[#0C0C0E] border border-white/15 text-sm leading-snug text-white/85 shadow-xl pointer-events-none"
              style={{
                top: pos.top,
                left: pos.left,
                transform: shift.flip
                  ? `translate(calc(-50% + ${shift.x}px), 44px)`
                  : `translate(calc(-50% + ${shift.x}px), calc(-100% - 8px))`,
              }}
            >
              <span className="block font-mono uppercase tracking-wider text-amber-400 mb-0.5">{type}</span>
              {text}
            </span>
          </>,
          document.body
        )}
    </span>
  )
}
